import { useCallback, useEffect, useRef, useState } from 'react';

import { toErrorMessage } from '../api/http';

/** State + controls for one screen's primary fetch. */
export interface AsyncData<T> {
  /** The loaded value, or null before the first successful load. */
  data: T | null;
  /** True while a load is in flight. */
  loading: boolean;
  /** User-safe error line, or null. */
  error: string | null;
  /** Re-run the fetch (Retry button / after a mutation). */
  reload: () => void;
  /** Patch the loaded value in place (post-mutation row updates). */
  setData: (updater: (prev: T) => T) => void;
}

/**
 * Load-once data hook shared by the admin screens: fetch on mount, expose
 * loading / error / retry, and let mutations patch rows locally instead of
 * refetching the whole list. The API may not exist yet in a dev environment
 * (built in parallel) — the error state IS the dev-time behavior then.
 *
 * `fetcher` must be referentially stable (the screens close over the useMemo'd
 * api facade), otherwise this refetches every render.
 */
export function useAsyncData<T>(fetcher: () => Promise<T>): AsyncData<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [generation, setGeneration] = useState<number>(0);
  const alive = useRef<boolean>(true);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    setError(null);
    fetcher().then(
      (value) => {
        if (alive.current) {
          setDataState(value);
          setLoading(false);
        }
      },
      (err: unknown) => {
        if (alive.current) {
          setError(toErrorMessage(err));
          setLoading(false);
        }
      }
    );
    return () => {
      alive.current = false;
    };
  }, [fetcher, generation]);

  const reload = useCallback(() => {
    setGeneration((g) => g + 1);
  }, []);

  const setData = useCallback((updater: (prev: T) => T) => {
    setDataState((prev) => (prev === null ? prev : updater(prev)));
  }, []);

  return { data, loading, error, reload, setData };
}
